import type { ComponentType } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRightLeft,
  CheckCircle2,
  Eye,
  GitPullRequest,
  MessageSquare,
  Send,
  UserRound,
} from "lucide-react";
import {
  actorLabel,
  externalStateSentence,
  linkNoun,
  stateLabel,
} from "./workflow-labels";
import type { Entry } from "./timeline-types";

export function iconFor(kind: string): ComponentType<{ className?: string }> {
  switch (kind) {
    case "comment":
      return MessageSquare;
    case "assigned":
      return UserRound;
    case "run_observed":
      return Eye;
    case "run_resolved":
      return CheckCircle2;
    case "linked":
    case "unlinked":
    case "external_state":
      return GitPullRequest;
    case "outbound_comment":
      return Send;
    default:
      return ArrowRightLeft;
  }
}

function str(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

const REASON_NOTE: Record<string, string> = {
  fix_didnt_hold: "the fix didn't hold",
  resurfaced: "it resurfaced",
  reopened: "reopened",
  not_relevant: "not relevant",
  walker_error: "the walker got it wrong",
  duplicate: "duplicate",
};

export function sentence(
  e: Entry,
  projectId: string,
  names: Map<string, string | null>,
): React.ReactNode {
  const who = actorLabel(e);
  const p = e.payload ?? {};
  const key = str(p.displayKey) ?? "";
  const item = `${linkNoun(str(p.linkKind) ?? "issue")} ${key}`.trim();
  const itemLink = str(p.url) ? (
    <a
      href={str(p.url) ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium underline-offset-4 hover:underline"
    >
      {item}
      <span className="sr-only"> (opens external tracker)</span>
    </a>
  ) : (
    <span className="font-medium">{item}</span>
  );

  switch (e.kind) {
    case "comment":
      return <span className="font-medium">{who} left a note</span>;
    case "assigned": {
      const to = str(p.to);
      return to ? (
        <>
          <span className="font-medium">{who}</span> assigned{" "}
          <span className="font-medium">{names.get(to) ?? "a member"}</span>
        </>
      ) : (
        <>
          <span className="font-medium">{who}</span> removed the assignee
        </>
      );
    }
    case "state": {
      const reason = e.reason ? REASON_NOTE[e.reason] : null;
      return (
        <>
          <span className="font-medium">{who}</span> moved it from{" "}
          {stateLabel(e.fromState)} to{" "}
          <span className="font-medium">{stateLabel(e.toState, e.reason)}</span>
          {reason && e.toState !== "dismissed" ? ` (${reason})` : ""}
        </>
      );
    }
    case "linked":
      return (
        <>
          <span className="font-medium">{who}</span>{" "}
          {p.created ? "created" : "linked"} {itemLink}
          {p.auto ? " (it names this finding)" : ""}
        </>
      );
    case "unlinked":
      return (
        <>
          <span className="font-medium">{who}</span> unlinked {itemLink}
        </>
      );
    case "external_state":
      return (
        <>
          {itemLink} {externalStateSentence(p)}
          {p.echo ? " (by iamu)" : ""}
        </>
      );
    case "outbound_comment":
      return (
        <>
          iamu commented on {itemLink}:{" "}
          {p.commentKind === "verified"
            ? "verified fixed by re-run"
            : p.commentKind === "dismissed"
              ? "dismissed here, ticket left open"
              : "the fix didn't hold, with evidence"}
        </>
      );
    case "run_observed":
    case "run_resolved": {
      const step = typeof p.stepNumber === "number" ? p.stepNumber : null;
      const run = e.walkthroughId ? (
        <Link
          to={`/projects/${projectId}/walkthroughs/${e.walkthroughId}${step !== null ? `?step=${step}` : ""}`}
          className="font-medium underline-offset-4 hover:underline"
        >
          {step !== null ? `run, step ${step}` : "a run"}
        </Link>
      ) : (
        "a run"
      );
      return e.kind === "run_observed" ? (
        <>
          Seen in {run}
          {e.actorName ? ` by ${e.actorName}` : ""}
        </>
      ) : (
        <>
          A re-run no longer saw it ({run}
          {e.actorName ? `, ${e.actorName}` : ""})
        </>
      );
    }
    default:
      return <span className="font-medium">{who} updated it</span>;
  }
}
